import { MAX_REVENUE_DAYS, RevenueDate } from "@cleanbudget/contract/revenue";
import { match } from "ts-pattern";
import { z } from "zod";

/**
 * The revenue page's range, as its URL keeps it: a preset (today, this week from Monday, this
 * month, this year, each up to today) or a custom `from`/`to`. Days are Dutch calendar days,
 * whatever the browser's time zone.
 */

export const RevenuePreset = z.enum(["today", "week", "month", "year", "custom"]);
export type RevenuePreset = z.infer<typeof RevenuePreset>;

export const RevenueSearch = z.object({
	preset: RevenuePreset.default("month").catch("month"),
	from: RevenueDate.optional().catch(undefined),
	to: RevenueDate.optional().catch(undefined),
});
export type RevenueSearch = z.infer<typeof RevenueSearch>;

const amsterdamDay = new Intl.DateTimeFormat("en-CA", {
	timeZone: "Europe/Amsterdam",
	year: "numeric",
	month: "2-digit",
	day: "2-digit",
});

/** Today in Amsterdam, `YYYY-MM-DD`. */
export const todayInAmsterdam = (now = new Date()): string => amsterdamDay.format(now);

const asUtc = (date: string) => new Date(`${date}T00:00:00Z`);
const isoDay = (date: Date) => date.toISOString().slice(0, 10);

/** `date` plus `days` (negative goes back). */
export const addDays = (date: string, days: number): string => {
	const day = asUtc(date);
	day.setUTCDate(day.getUTCDate() + days);
	return isoDay(day);
};

/** How many days from `from` to `to`, both included. */
export const daysIn = (from: string, to: string): number =>
	Math.round((asUtc(to).getTime() - asUtc(from).getTime()) / 86_400_000) + 1;

/** The range a search means. A custom range that is missing or backwards falls back to this month. */
export const rangeOf = (search: RevenueSearch, today = todayInAmsterdam()): { from: string; to: string } => {
	const monthStart = `${today.slice(0, 8)}01`;
	return (
		match(search)
			.with({ preset: "today" }, () => ({ from: today, to: today }))
			// Monday is 1, Sunday 0: back to the last Monday.
			.with({ preset: "week" }, () => ({
				from: addDays(today, -((asUtc(today).getUTCDay() + 6) % 7)),
				to: today,
			}))
			.with({ preset: "month" }, () => ({ from: monthStart, to: today }))
			.with({ preset: "year" }, () => ({ from: `${today.slice(0, 4)}-01-01`, to: today }))
			.with({ preset: "custom" }, ({ from, to }) =>
				from === undefined || to === undefined || from > to || daysIn(from, to) > MAX_REVENUE_DAYS
					? { from: monthStart, to: today }
					: { from, to },
			)
			.exhaustive()
	);
};
